import { AlertTriangle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import type { CampaignContactError, CampaignContactRef } from '@repo/shared-types';

interface ContactIssuesDialogProps {
  open: boolean;
  issues: CampaignContactError[];
  forcing: boolean;
  onOpenChange: (open: boolean) => void;
  onForce: () => void;
}

const describe = (ref: CampaignContactRef) =>
  `${ref.name ?? 'Unnamed'} (${ref.phone ?? 'no phone'})`;

/** Plain-English explanation of one issue, written for the person uploading. */
const explain = (issue: CampaignContactError): string => {
  if (issue.reason !== 'UNIQUE_IDENTIFIER_TAKEN' || !issue.identifier_owner) {
    return issue.message;
  }
  const owner = describe(issue.identifier_owner);
  const matched = issue.matched_contact
    ? ` The phone in the file already belongs to ${describe(issue.matched_contact)}, a different contact.`
    : '';
  return (
    `ID ${issue.unique_identifier} is already used by ${owner}, but the file gives it the ` +
    `phone ${issue.phone ?? 'no phone'}.${matched} Usually this means the person changed number.`
  );
};

/**
 * Lists the uploaded contacts the server could not save, explains each one,
 * and offers to force-update the existing contacts and create the campaign.
 */
export const ContactIssuesDialog = ({
  open,
  issues,
  forcing,
  onOpenChange,
  onForce,
}: ContactIssuesDialogProps) => {
  // Only identifier clashes can be forced; other database errors need a file fix.
  const canForce = issues.length > 0 && issues.every((i) => i.reason === 'UNIQUE_IDENTIFIER_TAKEN');

  return (
    <Dialog open={open} onOpenChange={(next) => !forcing && onOpenChange(next)}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="text-destructive h-5 w-5" />
            {issues.length === 1
              ? '1 contact has an issue'
              : `${issues.length} contacts have issues`}
          </DialogTitle>
          <DialogDescription>The campaign was not created. Nothing was saved.</DialogDescription>
        </DialogHeader>

        <ul className="space-y-3">
          {issues.map((issue) => (
            <li
              key={`${issue.position}-${issue.unique_identifier ?? ''}`}
              className="rounded-lg border p-3 text-sm"
            >
              <p className="font-medium">
                Contact #{issue.position}: {issue.name}{' '}
                <span className="text-muted-foreground font-normal">
                  {issue.phone ?? 'no phone'}
                  {issue.unique_identifier && ` · ID ${issue.unique_identifier}`}
                </span>
              </p>
              <p className="text-muted-foreground mt-1">{explain(issue)}</p>
              {issue.reason === 'UNIQUE_IDENTIFIER_TAKEN' && issue.identifier_owner && (
                <p className="mt-2 text-xs">
                  Force update will change{' '}
                  <span className="font-medium">{describe(issue.identifier_owner)}</span> to{' '}
                  <span className="font-medium">
                    {issue.name} ({issue.phone ?? 'no phone'})
                  </span>
                  .
                </p>
              )}
            </li>
          ))}
        </ul>

        {canForce ? (
          <p className="text-muted-foreground text-xs">
            Force update treats the ID as the person: each existing contact above takes the name and
            phone from your file, keeps its ID and history, and is added to the campaign. The old
            phone numbers will no longer be linked to these people.
          </p>
        ) : (
          <p className="text-destructive text-xs">
            Some contacts failed for a reason force update can&apos;t fix. Correct them in the file
            and upload again.
          </p>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={forcing}
          >
            Cancel
          </Button>
          {canForce && (
            <Button type="button" variant="destructive" onClick={onForce} disabled={forcing}>
              {forcing ? 'Updating...' : 'Force update & create campaign'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
